import * as React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Container from '@mui/material/Container'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputLabel from '@mui/material/InputLabel'
import EditableSelect from '../EditableSelect'
import Switch from '@mui/material/Switch'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'
import OutlinedInput from '@mui/material/OutlinedInput'
import FormHelperText from '@mui/material/FormHelperText'

import { slugifyString } from '../../utils/index'

import axios from 'axios'
import qs from 'qs'

import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'
import ImportingNotice from './ImportingNotice'
import { importErrorMessage } from './importError'


const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
}

const allExtractOptions = ['words', 'titles', 'hashtags']

const extractOptionsNames = {
    words: 'Content',
    titles: 'Titles',
    hashtags: 'Hashtags',
}

export default function Evernote(props) {
    const { username } = React.useContext(UserContext)

    const evernoteError = props.evernoteError || null

    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const [errorMessage, setErrorMessage] = React.useState(evernoteError)

    const [importError, setImportError] = React.useState(null)

    const handleAuthorizeClick = () => {
        window.location.href = '/evernote_oauth'
    }

    const handleDeauthorizeClick = () => {
        window.location.href = '/evernote_clear'
    }

    const handleNotebookChange = event => {
        setSelectedNotebook(event.target.value)
        setSelectedContext(generatePresetContext(event.target.value))
    }

    const [submittingForm, setSubmittingForm] = React.useState(false)

    const hasEvernoteAuthPermit =
        props.evernoteCookie || props.evernoteOauthAccessToken ? true : false

    const evernoteNotebooks = props.evernoteNotebooks || []

    const [selectedNotebook, setSelectedNotebook] = React.useState(
        evernoteNotebooks[0] || ''
    )

    const [selectedContext, setSelectedContext] = React.useState(
        generatePresetContext()
    )

    const [advancedSettings, setAdvancedSettings] = React.useState(false)

    const [extractOptions, setExtractOptions] = React.useState(
        allExtractOptions
    )

    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')

    const [replaceEntities, setReplaceEntities] = React.useState(false)

    const handleExtractChange = event => {
        const {
            target: { value },
        } = event
        setExtractOptions(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value
        )
    }

    const [noteTitles, setNoteTitles] = React.useState(0)

    const handleNoteTitlesChange = event => {
        setNoteTitles(event.target.value)
    }

    const [noteHashtags, setNoteHashtags] = React.useState(0)

    const handleNoteHashtagsChange = event => {
        setNoteHashtags(event.target.value)
    }

    const [partOfSpeechToProcess, setPartOfSpeechToProcess] = React.useState(
        'HASHTAGS_AND_WORDS'
    )

    const handlePartOfSpeechToProcessChange = event => {
        setPartOfSpeechToProcess(event.target.value)
    }

    const [numberOfNotes, setNumberOfNotes] = React.useState('200')

    const handleNumberOfNotesChange = event => {
        setNumberOfNotes(event.target.value)
    }

    const [includeUntitled, setIncludeUntitled] = React.useState(false)

    const submitDataForm = () => {
        const dataToSend = {
            source: 'evernote',
            evernoteOauthCode: props.evernoteOauthAccessToken,
            notebooks: selectedNotebook,
            context: selectedContext,
            extract: extractOptions,
            showTitlesOnGraph: noteTitles,
            includeUntitledTitles: includeUntitled ? '1' : '',
            hashtagsToMentions: noteHashtags,
            partOfSpeechToProcess: partOfSpeechToProcess,
            limit: numberOfNotes,
            selectedContexts: '',
            statementid: '',
            modifyAnalyzedText: modifyAnalyzedText,
            replaceEntities: replaceEntities,
        }

        const dataAsForm = qs.stringify(dataToSend)

        setSubmittingForm(true)
        axios
            .post('/import/evernote', dataAsForm, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    // If you're using window.location
                    window.location.href = response.data.redirectUrl

                    // Or, if you're using React Router's history (assuming you have access to the history object)
                    // history.push(response.data.redirectUrl);
                } else {
                    console.error(`Did't receive any response from the server.`)
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                    }
                    setSubmittingForm(false)
                }
            })
            .catch(error => {
                // Handle the error here
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(response.data.error)
                setSubmittingForm(false)
            })
    }

    return (
        <>
            <Box
                component="main"
                sx={{
                    backgroundColor: theme => theme.palette.background.default,
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'auto',
                    marginLeft: 4,
                }}
            >
                <Box
                    sx={{
                        pt: 2,
                        pb: 2,
                        mt: 6,
                        pl: '32px',
                        maxWidth: '800px',
                    }}
                >
                    <Box
                        sx={{
                            pb: 2,
                        }}
                    >
                        <Typography
                            component="subtitle1"
                            variant="subtitle1"
                            align="left"
                            color="text.primary"
                        >
                            Use InfraNodus to visualize your Evernote notes as a
                            graph, to make sense of the existing patterns and
                            generate insight and new ideas.
                        </Typography>
                    </Box>
                    {!hasEvernoteAuthPermit && (
                        <>
                            <Box
                                sx={{
                                    pb: 2,
                                }}
                            >
                                <Typography
                                    component="subtitle1"
                                    variant="subtitle1"
                                    align="left"
                                    color="text.primary"
                                    sx={{ mt: 2 }}
                                >
                                    Please, authorize InfraNodus in Evernote
                                    first to allow it access your notes. You can
                                    revoke this authorization at any time.
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="primary"
                                size="medium"
                                onClick={handleAuthorizeClick}
                            >
                                Authorize Evernote
                            </Button>
                        </>
                    )}
                    {hasEvernoteAuthPermit && (
                        <>
                            <Box mb={2} mt={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="medium"
                                    onClick={handleDeauthorizeClick}
                                >
                                    Reset Evernote Authorization
                                </Button>
                            </Box>
                            <Box sx={{ marginTop: 4 }}>
                                <Typography variant="subtitle1">
                                    Please, specify your Evernote import
                                    settings:
                                </Typography>
                            </Box>
                            <FormControl
                                fullWidth
                                sx={{
                                    marginTop: 4,
                                    marginBottom: 2,
                                }}
                            >
                                <InputLabel id="notebookSelect-label">
                                    1. Notebook to Analyze
                                </InputLabel>
                                <Select
                                    labelId="notebookSelect-label"
                                    id="notebookSelect"
                                    label="1. Notebook to Analyze"
                                    value={selectedNotebook}
                                    onChange={handleNotebookChange}
                                    sx={{ width: '380px' }}
                                >
                                    {evernoteNotebooks.map(
                                        (notebook, index) => (
                                            <MenuItem
                                                key={index}
                                                value={notebook}
                                            >
                                                {notebook}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                            </FormControl>
                            <FormControl
                                sx={{
                                    marginTop: 4,
                                    marginBottom: 2,
                                    width: '380px',
                                }}
                            >
                                <EditableSelect
                                    labelId="graphName-label"
                                    id="graphName"
                                    selectItems={contextNames}
                                    label={'2. Import to Graph'}
                                    initialValue={selectedContext}
                                    onChange={name => setSelectedContext(name)}
                                    sx={{ width: '380px' }}
                                />
                            </FormControl>

                            <FormControl
                                fullWidth
                                sx={{
                                    marginTop: 4,
                                    marginBottom: 0,
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={advancedSettings}
                                            onChange={event =>
                                                setAdvancedSettings(
                                                    event.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label="Show advanced settings"
                                />
                            </FormControl>

                            {advancedSettings && (
                                <>
                                    <FormControl
                                        sx={{ mt: 4, mb: 2, width: '380px' }}
                                    >
                                        <InputLabel id="extractOptions-label">
                                            3. Content to Import from Notes
                                        </InputLabel>
                                        <Select
                                            labelId="extractOptions-label"
                                            id="extractOptions"
                                            multiple
                                            label={
                                                '3. Content to Import from Notes'
                                            }
                                            value={extractOptions}
                                            onChange={handleExtractChange}
                                            input={
                                                <OutlinedInput label="Tag" />
                                            }
                                            renderValue={selected =>
                                                selected
                                                    .map(
                                                        item =>
                                                            extractOptionsNames[
                                                                item
                                                            ]
                                                    )
                                                    .join(', ')
                                            }
                                            MenuProps={MenuProps}
                                        >
                                            {allExtractOptions.map(
                                                extractOption => (
                                                    <MenuItem
                                                        key={extractOption}
                                                        value={extractOption}
                                                    >
                                                        <Checkbox
                                                            checked={
                                                                extractOptions.indexOf(
                                                                    extractOption
                                                                ) > -1
                                                            }
                                                        />
                                                        <ListItemText
                                                            primary={
                                                                extractOptionsNames[
                                                                    extractOption
                                                                ]
                                                            }
                                                        />
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                    </FormControl>
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <InputLabel id="modifyAnalyzedText-label">
                                            Build a knowledge graph from
                                        </InputLabel>
                                        <Select
                                            labelId="modifyAnalyzedText-label"
                                            id="modifyAnalyzedText"
                                            value={modifyAnalyzedText}
                                            onChange={event =>
                                                setModifyAnalyzedText(
                                                    event.target.value
                                                )
                                            }
                                            label="Build a knowledge graph from"
                                        >
                                            <MenuItem value={'none'} key="1">
                                                the words used in text
                                            </MenuItem>
                                            <MenuItem
                                                value={'detectEntities'}
                                                key="2"
                                            >
                                                the words and detected entities
                                            </MenuItem>
                                            <MenuItem
                                                value={'extractEntitiesOnly'}
                                                key="3"
                                            >
                                                only the detected entities
                                            </MenuItem>
                                        </Select>
                                        {modifyAnalyzedText != 'none' && (
                                            <FormHelperText
                                                sx={{ marginTop: 1 }}
                                            >
                                                By default, we extract the words
                                                used in your text. You can also
                                                choose to detect and tag
                                                entities in your text and build
                                                a graph from them.
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                    {modifyAnalyzedText != 'none' && (
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 2,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        id="replaceEntities"
                                                        checked={
                                                            replaceEntities
                                                        }
                                                        onChange={event =>
                                                            setReplaceEntities(
                                                                event.target
                                                                    .checked
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Replace entities with root concepts"
                                            />
                                            {replaceEntities && (
                                                <FormHelperText
                                                    id="replaceEntities-helperText"
                                                    sx={{ marginTop: 1 }}
                                                >
                                                    When turned on, we will not
                                                    only detect the entities,
                                                    but also replace them with
                                                    their ontological root
                                                    concepts throughout your
                                                    text.
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    )}
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <InputLabel id="noteTitlesSelect-label">
                                            4. How to Treat Notes' Titles
                                        </InputLabel>
                                        <Select
                                            labelId="noteTitlesSelect-label"
                                            id="noteTitlesSelect"
                                            label="4. How to Treat Notes' Titles"
                                            value={noteTitles}
                                            onChange={handleNoteTitlesChange}
                                            sx={{ width: '380px' }}
                                        >
                                            <MenuItem key={0} value={0}>
                                                add them to the graph's content
                                            </MenuItem>
                                            <MenuItem key={1} value={1}>
                                                show them on the graph as @tags
                                            </MenuItem>
                                        </Select>

                                        <FormHelperText>
                                            choose showing the notes on the
                                            graph as @tags if you want to
                                            explore connections between them
                                        </FormHelperText>
                                    </FormControl>
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 2,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={includeUntitled}
                                                    onChange={event =>
                                                        setIncludeUntitled(
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="include the 'Untitled' notes titles"
                                        />
                                    </FormControl>
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <InputLabel id="noteHashtagsSelect-label">
                                            5. How to Display the Notes'
                                            Hashtags
                                        </InputLabel>
                                        <Select
                                            labelId="noteHashtagsSelect-label"
                                            id="noteHashtagsSelect"
                                            label="5. How to Treat the Notes' Hasthags"
                                            value={noteHashtags}
                                            onChange={handleNoteHashtagsChange}
                                            sx={{ width: '380px' }}
                                        >
                                            <MenuItem key={0} value={0}>
                                                add them to the graph's content
                                            </MenuItem>
                                            <MenuItem key={1} value={1}>
                                                use them as @tags on the graph
                                            </MenuItem>
                                        </Select>

                                        <FormHelperText>
                                            choose showing the hashtags as @tags
                                            to see connections between your
                                            notes' tags
                                        </FormHelperText>
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <InputLabel id="posProcess-label">
                                            6. How to Process Hashtags in Notes
                                        </InputLabel>
                                        <Select
                                            labelId="posProcess-label"
                                            id="posProcess"
                                            label="6. How to Process Hashtags in Notes"
                                            value={partOfSpeechToProcess}
                                            onChange={
                                                handlePartOfSpeechToProcessChange
                                            }
                                            sx={{ width: '380px' }}
                                        >
                                            <MenuItem
                                                key={'HASHTAGS_AND_WORDS'}
                                                value={'HASHTAGS_AND_WORDS'}
                                            >
                                                process both hashtags and words
                                            </MenuItem>
                                            <MenuItem
                                                key={'HASHTAGS_ONLY'}
                                                value={'HASHTAGS_ONLY'}
                                            >
                                                process hashtags only, skip
                                                words
                                            </MenuItem>
                                        </Select>

                                        <FormHelperText>
                                            processing hashtags only will
                                            generate a graph that's more sparse
                                        </FormHelperText>
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <InputLabel id="limit-label">
                                            7. Maximum Number of Notes to
                                            Process
                                        </InputLabel>
                                        <Select
                                            labelId="limit-label"
                                            id="limit"
                                            label="7. Maximum Number of Notes to Process"
                                            value={numberOfNotes}
                                            onChange={handleNumberOfNotesChange}
                                            sx={{ width: '380px' }}
                                        >
                                            <MenuItem key={'20'} value={'20'}>
                                                20
                                            </MenuItem>
                                            <MenuItem key={'50'} value={'50'}>
                                                50
                                            </MenuItem>
                                            <MenuItem key={'100'} value={'100'}>
                                                100
                                            </MenuItem>
                                            <MenuItem key={'200'} value={'200'}>
                                                200
                                            </MenuItem>
                                            <MenuItem key={'300'} value={'300'}>
                                                300
                                            </MenuItem>
                                            <MenuItem key={'500'} value={'500'}>
                                                500
                                            </MenuItem>
                                            <MenuItem
                                                key={'1000'}
                                                value={'1000'}
                                            >
                                                1000
                                            </MenuItem>
                                            <MenuItem
                                                key={'2000'}
                                                value={'2000'}
                                            >
                                                2000
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </>
                            )}

                            {errorMessage && (
                                <Typography
                                    sx={{
                                        paddingTop: 4,
                                        paddingBottom: 4,
                                    }}
                                    color="error"
                                    component="div"
                                    gutterBottom
                                >
                                    {errorMessage}
                                </Typography>
                            )}
                            <FormControl
                                sx={{
                                    marginTop: 6,
                                    marginBottom: 4,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={submitDataForm}
                                >
                                    Analyze & Visualize
                                </Button>
                            </FormControl>

                            {submittingForm && (
                                <ImportingNotice
                                    linkHref={`/${username}/${selectedContext}/edit`}
                                    message="We are importing your data. Please, wait until this page reloads. It might take a while for large notebooks due to Evernote's rate limits. You will be able to find your graph at"
                                />
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </>
    )
}

function generatePresetContext(searchString) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    if (!searchString) return `evernote_${dateAndTime}`

    const twoFirstSearchQueryWords = slugifyString(
        searchString
            .split(' ')
            .slice(0, 2)
            .join('_')
            .slice(0, 20)
            .replace(/[^\P{L}a-z][^a-z]*/giu, '')
    )

    return `evernote_${twoFirstSearchQueryWords}_${dateAndTime}`
}
